import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import StudentRepository from '../services/StudentRepository.js';
import { Box, Button, List, Typography, TextField } from '@mui/material';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';
import OperationModal from '../components/common/OperationModal.jsx';
import EditableList from '../components/common/EditableList.jsx';
import DeleteDialog from '../components/common/DeleteDialog.jsx';

export default function Examinations({ repo, handleScreenNext, handleScreenBack, onSkip }) {
  const defaultValues = {
    name: null,
    year: null,
    month: null,
    rating: null,
  };
  /**
   * STATES
   */
  const [formValues, setFormValues] = React.useState(defaultValues);
  const [operation, setOperation] = React.useState('add');
  const [recordId, setRecordId] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isDialogOperationLoading, setIsDialogOperationLoading] = React.useState(false);
  const [exams, setExams] = React.useState([]);
  /**
   * FUNCTIONS
   */
  const handleValueChange = (evt) => {
    const name = evt.target.name;
    let value = evt.target.value;
    if (name == 'year') value = parseInt(value);
    if (name == 'rating') value = parseFloat(value);
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  const doOperation = () => {
    if (operation == 'update') return repo.updateExamination(recordId, formValues);
    return repo.addExamination(formValues);
  };
  const handleDialogClose = () => {
    setFormValues(defaultValues);
    setIsDialogOpen(false);
  };
  const updateExamList = () => {
    repo.getExams().then((data) => {
      setExams(data);
    });
  };
  const openAddItem = () => {
    setOperation('add');
    setIsModalOpen(true);
  };
  const openEditItem = (id, examDetails) => {
    setOperation('update');
    setFormValues({
      name: examDetails.name,
      year: examDetails.year,
      month: examDetails.month,
      rating: examDetails.rating,
    });
    setRecordId(id);
    setIsModalOpen(true);
  };
  const openDeleteItem = (id, examDetails) => {
    setFormValues({
      name: examDetails.name,
      year: examDetails.year,
      month: examDetails.month,
      rating: examDetails.rating,
    });
    setRecordId(id);
    setIsDialogOpen(true);
  };
  const handleDeleteItem = () => {
    setIsDialogOperationLoading(true);
    return repo.deleteExamination(recordId).then(() => {
      setIsDialogOperationLoading(false);
      setIsDialogOpen(false);
      setFormValues(defaultValues);
      updateExamList();
    });
  };
  useEffect(() => {
    updateExamList();
  }, []);
  /**
   * RENDER
   */
  return (
    <React.Fragment>
      <OperationModal
        open={isModalOpen}
        canSubmit={
          Boolean(formValues.name) &&
          Boolean(formValues.year) &&
          Boolean(formValues.month) &&
          Boolean(formValues.rating)
        }
        setIsModalOpen={setIsModalOpen}
        onSubmit={updateExamList}
        formValues={formValues}
        defaultValues={defaultValues}
        setFormValues={setFormValues}
        operation={doOperation}
      >
        <TextField
          required
          id="name"
          name="name"
          label="Examination Name"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.name ? formValues.name : ''}
          fullWidth
        />
        <TextField
          required
          id="year"
          name="year"
          type="number"
          label="Year"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.year ? formValues.year : ''}
          fullWidth
        />
        <TextField
          required
          id="month"
          name="month"
          label="Month"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.month ? formValues.month : ''}
          fullWidth
        />
        <TextField
          required
          id="rating"
          name="rating"
          label="Rating"
          type="number"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.rating ? formValues.rating : ''}
          fullWidth
        />
      </OperationModal>
      <DeleteDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleDelete={handleDeleteItem}
        recordId={recordId}
        recordDetails={formValues}
        loading={isDialogOperationLoading}
      />
      <Box justifyContent={'space-between'} display={'flex'} flexDirection={'row'}>
        <Box minWidth={'35vw'}>
          <Typography variant="formTitle" color={'rgba(0, 0, 0, 0.6)'}>
            Examinations Taken
          </Typography>
        </Box>

        <Box alignItems={'flex-end'}>
          <Button variant="contained" size="small" color="primary" onClick={openAddItem}>
            <Typography variant="smallText">Add Record</Typography>
          </Button>
        </Box>
      </Box>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {exams &&
          exams.map((exam, index) => (
            <EditableList
              key={index}
              itemId={exam.id}
              primaryText={exam.name}
              subtitleText={
                <React.Fragment>
                  Rating: {exam.rating} · {exam.month} {exam.year}
                </React.Fragment>
              }
              addDivider={index < exams.length - 1}
              handleEdit={() => openEditItem(exam.id, exam)}
              handleDelete={() => openDeleteItem(exam.id, exam)}
            />
          ))}
      </List>
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={onSkip}
            sx={{ marginRight: '10px' }}
          >
            Skip to Finish
          </Button>
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={() => handleScreenNext()}
            >
              Next
            </Button>
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

Examinations.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  studentId: PropTypes.string,
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleErrors: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  onSkip: PropTypes.func,
};
